/**
 * TabLayout Component
 * 
 * Manages the tab navigation interface
 * 
 * @module components/TabLayout
 */

import AppState from '../core/AppState.js';
import EventBus, { AppEvents } from '../core/EventBus.js';
import Config from '../core/Config.js';
import { filterTabs } from '../core/AccessControl.js';
import { bindPromptEditors } from './PromptBinder.js';
import { icon as devIcon } from '../ui/icons.js';
import { renderSegmentedTabs } from './SegmentedTabs.js';
import { installSplit } from '../utils/Splitter.js';

export class TabLayout {
  constructor() {
    this.container = null;
    this.activeTab = 'strategic-framework';
  }
  
  async initialize() {
    // Get current tab from AppState or set default
    const currentTab = AppState.getState('currentTab');
    if (currentTab) {
      this.activeTab = currentTab;
    } else {
      // Ensure AppState has the correct initial tab
      AppState.setState({ currentTab: this.activeTab });
    }
    
    console.log('📑 TabLayout initialized with tab:', this.activeTab);
    
    // Subscribe to state changes
    AppState.subscribe('currentTab', (tabId) => {
      if (tabId && tabId !== this.activeTab) {
        console.log('📑 TabLayout received tab change:', tabId);
        this.activeTab = tabId;
        this.render();
      }
    });
  }
  
  mount(container) {
    this.container = container;
    this.render();
  }
  
  render() {
    if (!this.container) return;
    
    // Ensure we have a global tab-row host
    const headerHost = document.getElementById('tab-row');
    
    const visibleTabs = filterTabs(Config.TABS);
    if (!visibleTabs.find(t => t.id === this.activeTab)) {
      this.activeTab = visibleTabs[0]?.id || this.activeTab;
      try { AppState.setState({ currentTab: this.activeTab }); } catch(_) {}
    }

    // Render header into global host for consistent layout using SegmentedTabs
    if (headerHost) {
      renderSegmentedTabs(headerHost, visibleTabs, this.activeTab, (id)=>this.switchTab(id));
    }

    // Render content only into the container
    this.container.innerHTML = `
      <div class="tab-layout">
        <div class="tab-content">
          <div class="tab-panel active" id="tab-panel-${this.activeTab}">
            ${this.renderTabContent(this.activeTab)}
          </div>
        </div>
      </div>`;
    
    // Attach event handlers
    this.attachEventHandlers();

    // Bind prompt editors in case header buttons exist on Objectives
    try { if (this.activeTab === 'objectives') bindPromptEditors(this.container); } catch (_) {}
  }

  renderTabIcon(tab) {
    // Enterprise theme: hide tab icons for a clean segmented control
    try { if (Config?.FEATURES?.THEME_ENTERPRISE === true) return ''; } catch(_) {}
    switch (tab.id) {
      case 'strategic-framework':
        return devIcon('compass', { size: 18 });
      case 'objectives':
        return devIcon('target', { size: 18 });
      case 'portfolio':
        return devIcon('trending-up', { size: 18 });
      case 'insights':
        return devIcon('message-square', { size: 18 });
      case 'business-levers':
        return devIcon('rotate', { size: 18 });
      case 'settings':
        return devIcon('settings', { size: 18 });
      default:
        return tab.icon || '';
    }
  }
  
  renderTabContent(tabId) {
    switch(tabId) {
      case 'strategic-framework':
        return `
          <div id="strategy-section"></div>
        `;
        
      case 'objectives':
        return `
          <div class="objectives-initiatives">
            <div class="objectives-header">
              <div class="prompt-editors-top">
                <button class="prompt-edit-btn" 
                        data-prompt-key="outcomeAssistant" 
                        data-prompt-name="Strategic Assistant">
                  <span>Edit Strategic Assistant</span>
                </button>
                <button class="prompt-edit-btn"
                        data-prompt-key="initiativeExtractor"
                        data-prompt-name="Initiative Extractor">
                  <span>Edit Initiative Extractor</span>
                </button>
              </div>
            </div>
            <div class="objectives-split-layout" data-split-container>
              <div id="chat-section"></div>
              <div id="initiatives-section"></div>
            </div>
          </div>
        `;
        
      case 'portfolio':
        return `
          <div id="portfolio-section"></div>
        `;
      case 'insights':
        return `
          <div id="insights-section"></div>
        `;
      case 'business-levers':
        return `
          <div id="business-levers-section"></div>
        `;
        
      case 'settings':
        return `
          <div id="settings-section"></div>
        `;
        
      default:
        return '<p>Unknown tab</p>';
    }
  }
  
  attachEventHandlers() {
    const blockTabs = ((typeof window !== 'undefined' && window.__BLOCK_TAB_CHANGES__ === true)
      || (typeof localStorage !== 'undefined' && localStorage.getItem('blockTabChanges') === 'true'))
      && (import.meta.env?.DEV);
    if (!blockTabs) {
      // Handlers are attached by SegmentedTabs; nothing else needed here.
    } else {
      console.warn('⛔ Tab changes blocked (DEV diagnostic mode)');
    }
    
    // Mount modules to their containers after render
    this.mountModules();

    // Make Objectives split resizable when present
    try {
      if (this.activeTab === 'objectives') installSplit('.objectives-split-layout', 'objectives');
    } catch (_) {}

    // Normalize Settings to use same split with visible divider
    try {
      if (this.activeTab === 'settings') installSplit('.settings-grid-layout-simplified', 'settings');
    } catch (_) {}
  }
  
  switchTab(tabId) {
    const blockTabs = ((typeof window !== 'undefined' && window.__BLOCK_TAB_CHANGES__ === true)
      || (typeof localStorage !== 'undefined' && localStorage.getItem('blockTabChanges') === 'true'))
      && (import.meta.env?.DEV);
    if (blockTabs) return; // ignore tab switches in diagnostic mode
    // Validate tab ID
    const validTabs = filterTabs(Config.TABS).map(tab => tab.id);
    if (!validTabs.includes(tabId)) {
      console.error('Invalid tab ID:', tabId, 'Valid tabs:', validTabs);
      return;
    }
    
    console.log('📑 TabLayout switching to tab:', tabId);
    
    // Update local state
    this.activeTab = tabId;
    
    // Update global state
    AppState.setState({ currentTab: tabId });
    
    // Emit event for other components
    EventBus.emit(AppEvents.TAB_CHANGED, tabId);
    
    // Re-render immediately to show active state
    this.render();
  }
  
  mountModules() {
    // Mount strategy module if on strategic framework tab
    if (this.activeTab === 'strategic-framework') {
      const strategySection = document.getElementById('strategy-section');
      if (strategySection && window.App?.moduleManager) {
        window.App.moduleManager.mount('strategy', strategySection);
      } else if (strategySection && window.App) {
        // Legacy fallback
        const strategyModule = window.App.getModule('strategy');
        strategyModule?.mount(strategySection);
      }
    }
    
    // Mount chat module if on objectives tab
    if (this.activeTab === 'objectives') {
      const chatSection = document.getElementById('chat-section');
      const initiativesSection = document.getElementById('initiatives-section');
      
      if (chatSection && window.App?.moduleManager) {
        window.App.moduleManager.mount('chat', chatSection);
        try { window.App.moduleManager.activate('chat'); } catch (_) {}
      } else if (chatSection && window.App) {
        const chatModule = window.App.getModule('chat');
        chatModule?.mount(chatSection);
        try { chatModule?.activate?.(); } catch (_) {}
      }
      
      if (initiativesSection && window.App?.moduleManager) {
        window.App.moduleManager.mount('initiatives', initiativesSection);
        try { window.App.moduleManager.activate('initiatives'); } catch (_) {}
      } else if (initiativesSection && window.App) {
        const initiativesModule = window.App.getModule('initiatives');
        initiativesModule?.mount(initiativesSection);
        try { initiativesModule?.activate?.(); } catch (_) {}
      }
    }
    
    // Mount portfolio module
    if (this.activeTab === 'portfolio') {
      const portfolioSection = document.getElementById('portfolio-section');
      if (portfolioSection && window.App?.moduleManager) {
        window.App.moduleManager.mount('portfolio', portfolioSection);
      } else if (portfolioSection && window.App) {
        const portfolioModule = window.App.getModule('portfolio');
        portfolioModule?.mount(portfolioSection);
      }
    }
    
    // Mount insights module
    if (this.activeTab === 'insights') {
      const insightsSection = document.getElementById('insights-section');
      if (insightsSection && window.App?.moduleManager) {
        window.App.moduleManager.mount('insights', insightsSection);
      } else if (insightsSection && window.App) {
        const insightsModule = window.App.getModule('insights');
        insightsModule?.mount(insightsSection);
      }
    }

    // Mount business levers module
    if (this.activeTab === 'business-levers') {
      const leversSection = document.getElementById('business-levers-section');
      if (leversSection && window.App?.moduleManager) {
        window.App.moduleManager.mount('businessLevers', leversSection);
      } else if (leversSection && window.App) {
        const leversModule = window.App.getModule('businessLevers');
        leversModule?.mount(leversSection);
      }
    }

    // Mount settings module
    if (this.activeTab === 'settings') {
      const settingsSection = document.getElementById('settings-section');
      if (settingsSection && window.App?.moduleManager) {
        window.App.moduleManager.mount('settings', settingsSection);
      } else if (settingsSection && window.App) {
        const settingsModule = window.App.getModule('settings');
        settingsModule?.mount(settingsSection);
      }
    }
  }
}
